import {Subject} from 'rxjs';
import {sameDay, todayDateOnly, toInterfaceArray, toJSONLocal, toLocalDate} from '../helpers/helpers';
import {numbers} from '../questionnaire/numbers';
import {
  IQuestionnaire,
  Questionnaire,
  QuestionnaireChangeEvent,
  QuestionnaireChangeType
} from '../questionnaire/questionnaireClassesAndEnums';
import {IInfoHubFavorites, InfoHubFavorites} from './InfoHubFavorites';
import {IMealFood} from './food';
import {CaloryPercentage} from './FoodComposition';
import {FoodHistory, FoodHistoryChangedEvent, FoodHistoryChangeType, IFoodHistory} from './FoodHistory';
import {IMealFoodProduct} from './FoodProduct';
import {MealType} from './Meal';
import {INotificationSettings, NotificationSettings} from './NotificationSettings';
import {IMealRecipe, IRecipe, Recipe} from './Recipe';
import {ITrackedDay, TrackedDay} from './TrackedDay';
import {ChangeMealItem, MealItem} from './MealItem';
import {TutorialsDone} from './TutorialsDone';
import {FoodType} from './FoodRecord';
import {UUID} from 'angular2-uuid';
import {ILocalUserRecipe} from './IngredientMapping/SpeechToMealResponse';

export class User {
  // general data
  id: string;
  isLocalUser = false;
  userName: string;
  email: string;
  isActive: boolean;
  identitiyProviderInfos: IdentitiyProviderInfo[];
  claims: object[];
  averageCaloryPercentages: CaloryPercentage;
  typicalMealsAutoSet: boolean;
  typicalDailyEmissionsAutoSet: boolean;
  trackedDays: TrackedDay[] = [];
  readonly typicalBreakfastCaloriePercentage: number = 0.2;
  readonly typicalLunchCaloriePercentage: number = 0.35;
  readonly typicalDinnerCaloriePercentage: number = 0.35;

  // nutrition data
  readonly typicalSnacksCaloriePercentage: number = 0.1;
  // tracking
  lastChangedAt: Date;
  lastChangeId: string;
  createdAt: Date;
  public changedSubject: Subject<UserChangedEvent> = new Subject<UserChangedEvent>();

  constructor(i?: IUser) {

    if (i) {
      // this.averageCaloryPercentages = i.averageCaloryPercentages;
      this._typicalMeals = i.typicalMeals;
      if (i.typicalMealsAutoSet) {
        this.typicalMealsAutoSet = i.typicalMealsAutoSet;
      }
      this.typicalDailyEmissions = i.typicalDailyEmissions;
      if (i.typicalDailyEmissionsAutoSet) {
        this.typicalDailyEmissionsAutoSet = i.typicalDailyEmissionsAutoSet;
      }
      if (i.recipes) {
        this._recipes = [];
        i.recipes.forEach(recipe => {
          const temp = new Recipe(recipe);
          this._recipes.push(temp);
        });
      }
      this.trackedDays = [];
      if (i.trackedDays) {
        i.trackedDays.forEach(iTrackedDay => {
          this.trackedDays.push(new TrackedDay(iTrackedDay, i.typicalMeals, this.recipes));
        });
      }
      this.questionnaire = new Questionnaire(i.questionnaire);
      this.id = i.id;
      this.isLocalUser = i.isLocalUser;
      this.userName = i.userName;
      this.email = i.email;
      this.lastChangedAt = new Date(i.lastChangedAt);
      this.lastChangeId = i.lastChangeId;
      this.isActive = i.isActive;
      this._emailVerified = i.emailVerified;
      this.createdAt = new Date(i.createdAt);
      this.identitiyProviderInfos = i.identitiyProviderInfos;
      this._notificationSettings = new NotificationSettings(i.notificationSettings);
      this._tutorialsDone = i.tutorialsDone;
      if (i.daysUsed) {
        this._daysUsed = [];
        i.daysUsed.forEach(d => {
          this._daysUsed.push(toLocalDate(d));
        });
      }
      this.claims = i.claims;

      if (i.foodHistory) {
        this._foodHistory = new FoodHistory(i.foodHistory);
        this.newFoodHistoryAssignment();
      }

      if (i.infoHubFavorites) {
        this._infoHubFavorites = new InfoHubFavorites(i.infoHubFavorites);
      }
      this._fiveDaysAppUsageFeedbackShown = i.fiveDaysAppUsageFeedbackShown;

      // Check for missing tracked days
      this.checkMissingDays();
    }

    // Here a local user is initialized
    if (!this.id) {
      this.id = UUID.UUID();
      this.isLocalUser = true;
      this.createdAt = new Date();
      this.lastChangedAt = this.createdAt;
      this.lastChangeId = UUID.UUID();
    }

    // Typical meals must always be there. They are initialized with standard meals if not provided
    if (!this.typicalMeals || this.typicalMeals.length === 0) {
      this.initializeTypicalMeals();
      this.typicalMealsAutoSet = true;
    }
    this.initializeTypicalMealCalories();
    // Also typical calories and typical emissions should be there. If not set, take German defaults
    if (!this.typicalDailyEmissions) {
      this._typicalDailyEmissions = numbers.ger_avg_emissions_per_calorie * this.dailyCalories;
      this.typicalDailyEmissionsAutoSet = true;
    }
    // Questionnaire must be always there. Initialize if not set
    if (!this.questionnaire) {
      this.questionnaire = new Questionnaire();
    }
    // Food history must be always there. Initialize if not set
    if (!this.foodHistory) {
      this._foodHistory = new FoodHistory();
      this.newFoodHistoryAssignment();
    }
    if (this.notificationSettings === undefined) {
      this._notificationSettings = new NotificationSettings();
    }
    this.newNotificationSettingsAssignment();
    if (!this.infoHubFavorites) {
      this._infoHubFavorites = new InfoHubFavorites();
    }
    this.newInfoHubFavoritesAssignment();

    this.addUnitsForAllRecipeFoodLogs();
  }

  private _emailVerified: boolean;

  public get emailVerified(): boolean {
    return this._emailVerified || this.isLocalUser || !this.isInternalUser;
  }

  public get isInternalUser(): boolean {
    if (!this.identitiyProviderInfos || this.identitiyProviderInfos.length === 0) {
      return true;
    }
    return !!this.identitiyProviderInfos.find(i => i.identityProvider === 'idoplanty');
  }

  private readonly _notificationSettings: NotificationSettings;

  public get notificationSettings(): NotificationSettings {
    return this._notificationSettings;
  }

  private _tutorialsDone: TutorialsDone;

  public get tutorialsDone(): TutorialsDone {
    return this._tutorialsDone;
  }

  // daily calories are taken from the questionnaire. if not set there yet, initialize them with german average
  get dailyCalories(): number {
    return (this.questionnaire && this.questionnaire.calories > 0)
        ? this.questionnaire.calories : numbers.ger_avg_calories;
  }

  private _typicalMeals: TypicalMeal[];

  public get typicalMeals(): TypicalMeal[] {
    return this._typicalMeals;
  }

  public set typicalMeals(value) {
    this._typicalMeals = value;
    this.typicalMealsAutoSet = false;
    this.updateTypicalMealCalories();
    this.checkCurrentTrackedDayHasNoMealsYet();
  }

  private _typicalDailyEmissions: number;

  public get typicalDailyEmissions(): number {
    return this._typicalDailyEmissions;
  }

  public set typicalDailyEmissions(value) {
    this._typicalDailyEmissions = (value !== undefined && value !== null) ? value : 0;
    this.typicalDailyEmissionsAutoSet = false;
    this.changedSubject.next(new UserChangedEvent(UserChangeType.changed_emissions_target,
        this._typicalDailyEmissions));
  }

  public get todaysTrackedDay(): TrackedDay {
    const index = this.getTrackedDay(new Date());
    return this.trackedDays[index];
  }

  private _questionnaire: Questionnaire;

  get questionnaire(): Questionnaire {
    return this._questionnaire;
  }

  set questionnaire(value: Questionnaire) {
    this._questionnaire = value;
    this.newQuestionnaireAssignment();
  }

  private _recipes: Recipe[] = [];

  public get recipes(): Recipe[] {
    return this._recipes.sort((a, b) => a.name > b.name ? 1 : (a.name < b.name ? -1 : 0));
  }

  private readonly _foodHistory: FoodHistory;

  public get foodHistory(): FoodHistory {
    return this._foodHistory;
  }

  private _fiveDaysAppUsageFeedbackShown: boolean;

  public get fiveDaysAppUsageFeedbackShown(): boolean {
    return this._fiveDaysAppUsageFeedbackShown;
  }

  private readonly _daysUsed: Date[] = [];
  public get daysUsed(): Date[] {
    return this._daysUsed;
  }

  // onboarding
  get caloriesOnboarded(): boolean {
    return (this.questionnaire && this.questionnaire.calories > 0);
  }

  get questionnaireOnboarded(): boolean {
    return (this.questionnaire && this.questionnaire.allQuestionsAnwered);
  }

  get emissionsTargetOnboarded(): boolean {
    return !this.typicalDailyEmissionsAutoSet;
  }

  get onboardingFinished(): boolean {
    return (this.caloriesOnboarded && this.emissionsTargetOnboarded);
  }

  // favorites from info hub
  private readonly _infoHubFavorites: InfoHubFavorites;
  get infoHubFavorites(): InfoHubFavorites {
    return this._infoHubFavorites;
  }

  private get autofillEmissions(): number {
    return Math.max(this.typicalDailyEmissions,
        numbers.ger_avg_emissions_per_calorie * this.dailyCalories);
  }

  public getTrackedDay(date: Date): number {
    if (!this.trackedDays) {
      return undefined;
    }
    let selectedDate = new Date();
    // Harmonize dates
    selectedDate.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    // Search for tracked day with requested date
    // console.log("searching for tracked day: "+date);
    let i = 0;
    let foundIndex = -1;
    this.trackedDays.forEach(trackedDay => {
      // Comparison with getTime needed as date objects with the same date are not equal objects
      selectedDate = trackedDay.day;
      selectedDate.setHours(0, 0, 0, 0);
      // console.log("comparing "+trackedDay.day +" with "+ date);
      // console.log("comparing "+trackedDay.day.getTime() +" with "+ date.getTime());
      if (trackedDay.day.getTime() === date.getTime()) {
        // console.log("     *****trackedDay found******")
        foundIndex = i;
      }
      i++;
    });
    // If nothing was found for that date (e.g. new day) create empty one end return it
    if (foundIndex === -1) {
      const newDay: TrackedDay = new TrackedDay(undefined, this.typicalMeals);
      newDay.day = new Date(date);
      this.storeTrackedDay(newDay, undefined, undefined, undefined, undefined);
      console.log('creating new index value ' + i);
      return 0;
    } else {
      //   console.log("returning existing index "+foundIndex);
      return foundIndex;
    }
  }

  public getAllTrackedDays(): TrackedDay[] {
    // var trackedDaysPromise: Promise<string> = this.fetchTrackedDays();
    // var trackedDays: TrackedDay[];
    // trackedDaysPromise.then((val: string) => {
    //   if(val){
    //     console.log(val);
    //     trackedDays = JSON.parse(val);
    //   }else{
    //       console.log("TrackedDays-Promise returned but no value for tracked days");
    //     }
    // })
    // console.log(trackedDays)
    // return trackedDays;
    return this.trackedDays;
  }

  public getdaysUsed(): Date[] {
    return this.daysUsed;
  }

  public checkDaysUsed() {
    // Create a new instance and harmonize
    const newDay: Date = new Date();
    newDay.setHours(0, 0, 0, 0);
    // Check if day is already in the array
    let found = false;
    this.daysUsed.forEach(d => {
      if (d.getTime() === newDay.getTime()) {
        found = true;
      }
    });
    if (!found) {
      this._daysUsed.push(newDay);
      this.changedSubject.next(new UserChangedEvent(UserChangeType.added_used_day, this.daysUsed));
    }
  }

  public storeTrackedDay(trackedDay: TrackedDay, addedItems: MealItem[], deletedItems: MealItem[],
                         changedItems: ChangeMealItem[], mealType: MealType) {
    if (!this.trackedDays) {
      return;
    }
    // Harmonize date
    trackedDay.day.setHours(0, 0, 0, 0);
    // Try to find that day, then update it
    let found = false;
    this.trackedDays.forEach(element => {
      // console.log("**** comparing "+trackedDay.day.getTime() +" "+ element.day.getTime());
      if (trackedDay.day.getTime() === element.day.getTime()) {
        element = trackedDay;
        found = true;
        element.mealChanged();
      }
    });
    // If not found, then append it
    if (!found) {
      // store new days at index 0 so we can access without iterating
      this.trackedDays.unshift(trackedDay);
      // Publish change
      this.changedSubject.next(new UserChangedEvent(UserChangeType.added_tracked_day, trackedDay));
      return;
    }
    // update logs for added items
    if (addedItems !== undefined) {
      console.log('**** logging ' + addedItems.length + ' items ****');
      addedItems.forEach(element => {
        this._foodHistory.logItem(element, mealType);
      });
    }
    // update logs for deleted items
    if (deletedItems !== undefined) {
      console.log('**** deleting logs of ' + deletedItems.length + ' items ****');
      deletedItems.forEach(element => {
        this._foodHistory.deleteItemLog(element, mealType, trackedDay.day);
      });
    }
    // update logs for changed items
    if (changedItems !== undefined) {
      console.log('**** changing ' + changedItems.length + ' items ****');
      changedItems.forEach(element => {
        this._foodHistory.changeItemLog(element.oldItem, element.newItem, mealType, trackedDay.day);
      });
    }

    // Publish change
    this.changedSubject.next(new UserChangedEvent(UserChangeType.changed_tracked_day, trackedDay));
    return;
  }

  public toInterface(): IUser {
    const iTrackedDays: ITrackedDay[] = [];
    if (this.trackedDays) {
      this.trackedDays.forEach(trackedDay => {
        iTrackedDays.push(trackedDay.toInterface());
      });
    }
    const iDaysUsed: string[] = [];
    if (this.daysUsed) {
      this.daysUsed.forEach(dayUsed => {
        iDaysUsed.push(toJSONLocal(dayUsed));
      });
    }
    return {
      typicalMeals: this.typicalMeals,
      typicalMealsAutoSet: this.typicalMealsAutoSet,
      typicalDailyEmissions: this.typicalDailyEmissions,
      typicalDailyEmissionsAutoSet: this.typicalDailyEmissionsAutoSet,
      trackedDays: iTrackedDays,
      questionnaire: this.questionnaire.toInterface(),
      id: this.id,
      isLocalUser: this.isLocalUser,
      userName: this.userName,
      email: this.email,
      lastChangedAt: this.lastChangedAt,
      lastChangeId: this.lastChangeId,
      isActive: this.isActive,
      emailVerified: this._emailVerified,
      createdAt: this.createdAt,
      identitiyProviderInfos: this.identitiyProviderInfos,
      daysUsed: iDaysUsed,
      claims: this.claims,
      recipes: toInterfaceArray(this.recipes),
      foodHistory: this.foodHistory.toInterface(),
      notificationSettings: this._notificationSettings.toInterface(),
      infoHubFavorites: this.infoHubFavorites.toInterface(),
      tutorialsDone: this.tutorialsDone,
      fiveDaysAppUsageFeedbackShown: this._fiveDaysAppUsageFeedbackShown
    };
  }

  public setTutorialsDone(tutorialsDone: TutorialsDone, updateBackend: boolean = true): void {
    this._tutorialsDone = tutorialsDone;
    if (updateBackend) {
      this.changedSubject.next(new UserChangedEvent(UserChangeType.changed_tutorials_done, tutorialsDone));
    }
  }

  public emailWasVerified(): void {
    this._emailVerified = true;
  }

  // adds missing days and autofills zero-emission days in the past with avg german emissions / personal emissions target

  public checkAutofill(): void {
    this.trackedDays.forEach(trackedDay => {
      if (!trackedDay.autofilled && !trackedDay.edited && !sameDay(trackedDay.day, new Date())) {
        trackedDay.autofill(this.dailyCalories, this.autofillEmissions);
        this.changedSubject.next(new UserChangedEvent(UserChangeType.autofill, trackedDay));
      }
    });
  }

  public setFiveDaysAppUsageFeedbackShown(): void {
    this._fiveDaysAppUsageFeedbackShown = true;
    this.changedSubject.next(new UserChangedEvent(UserChangeType.set_five_days_app_usage_feedback_shown));
  }

  public forceUpdateEvent(): void {
    this.changedSubject.next(new UserChangedEvent(UserChangeType.miscellaneous));
  }

  public addRecipe(recipe: Recipe) {
    console.log('User checking recipe: ');
    console.log(recipe);
    if (this.recipes === undefined) {
      this._recipes = [];
    }  // for first-time adding
    let foundIndex = -1;
    this.recipes.forEach((element, index) => {
      if (element.id === recipe.id) {
        foundIndex = index;
      }
    });
    if (foundIndex >= 0) {
      this.recipes[foundIndex] = recipe;
      this.changedSubject.next(new UserChangedEvent(UserChangeType.changed_recipe, recipe));
    } else {
      this._recipes.push(recipe);
      this.changedSubject.next(new UserChangedEvent(UserChangeType.added_recipe, recipe));
    }
  }

  public deleteRecipe(input: string) {
    console.log('delete recipe request received for ID: ' + input);
    this.recipes.forEach((element, index) => {
      if (element.id === input) {
        this.recipes.splice(index, 1);
        this.changedSubject.next(new UserChangedEvent(UserChangeType.deleted_recipe, input));
      }
    });
  }

  public updateRecipeImage(id: string, imageId: string): void {
    const recipe = this._recipes.find(r => r.id === id);
    if (!recipe) {
      console.error('No recipe with that id exists!');
      return;
    }
    recipe.mainImage = imageId;
    console.log('Recipe image id adjusted. Now raising user changed event');
    this.changedSubject.next(new UserChangedEvent(UserChangeType.only_storage_update, undefined, true));
  }

  public getAllRecipes(): Recipe[] {
    return this.recipes;
  }

  public getLocalUserRecipes(): ILocalUserRecipe[] {
    if (!this.isLocalUser) {
      return undefined;
    }
    return this.recipes.map(r => r.toILocalUserRecipe());
  }

  public isPartOfTypicalMeal(item: MealItem, mealType: MealType): boolean {
    if (!this.typicalMeals || this.typicalMeals.length === 0) {
      return false;
    }
    const typicalMeal = this.typicalMeals.find(t => t.type === mealType);
    if (!typicalMeal || !typicalMeal.typicalMealItems) {
      return false;
    }
    for (const typicalMealItem of typicalMeal.typicalMealItems) {
      if (item.equals(MealItem.fromDetailedInterface(typicalMealItem))) {
        return true;
      }
    }
    return false;
  }

  // in case it's higher than that
  private checkMissingDays() {
    if (this.createdAt === undefined) {
      console.log('****CRITICAL ERROR***** USER Start Date not found');
      return;
    }
    const date: Date = new Date(this.createdAt);
    date.setHours(0, 0, 0, 0);
    const today: Date = new Date();
    today.setHours(0, 0, 0, 0);
    let i = 1000;
    // loop terminates when startDate is reached, since older TDs impossible
    // "i" variable only here to avoid infinite while loop if startDate problems
    while (date.getTime() <= today.getTime() && i >= 0) {
      // if(this.trackedDays.find(td => td.day.getTime() === date.getTime()) === undefined){
      //     console.log("missing day found: "+date);
      //     var missingDay: TrackedDay = new TrackedDay(undefined, this.typicalMeals);
      //     missingDay.day = new Date(date);
      //     this.trackedDays.unshift(missingDay);
      // }
      this.getTrackedDay(date);
      date.setDate(date.getDate() + 1);
      i--;
    }
  }

  private newQuestionnaireAssignment() {
    // Subscribe to the change events of this questionnaire instance, to always get notified if something was changed
    this.questionnaire.changedSubject.subscribe((event: QuestionnaireChangeEvent) => {
      if (event) { // this is tricky, because event is an enum with the valid value 0. But if(event) is false in case of event === 0 !!!
        this.onQuestionnaireChanged(event);
      }
    });
  }

  private newFoodHistoryAssignment() {
    // Subscribe to the change events of this food history instance, to always get notified if something was changed
    this.foodHistory.changedSubject.subscribe((event: FoodHistoryChangedEvent) => {
      if (event) {
        this.onFoodHistoryChanged(event);
      }
    });
  }

  private newNotificationSettingsAssignment() {
    // Subscribe to the change events of this notification settings instance, to always get notified if something was changed
    this.notificationSettings.changedSubject.subscribe(event => {
      if (event) {
        this.changedSubject.next(new UserChangedEvent(UserChangeType.changed_notificaton_settings, this.notificationSettings));
      }
    });
  }

  private newInfoHubFavoritesAssignment() {
    // Subscribe to the change events of this favorites instance, to always get notified if something was changed
    this.infoHubFavorites.changedSubject.subscribe(event => {
      if (event) {
        this.changedSubject.next(new UserChangedEvent(UserChangeType.changed_info_hub_favorites, this.infoHubFavorites));
      }
    });
  }

  // Method which is called after the questionnaire did change
  private onQuestionnaireChanged(change: QuestionnaireChangeEvent) {
    // If calories are changed, calories per meal and emissions-budget must be updated too
    if (change.type === QuestionnaireChangeType.calories) {
      this.updateTypicalMealCalories();
      if (this.emissionsTargetOnboarded) {
        this.updateEmissionsBudgetWithNewCalories(change.previousValue, change.newValue);
      }
    }
    this.changedSubject.next(new UserChangedEvent(UserChangeType.changed_questionnaire, this.questionnaire));
  }

  // Method which is called after the food history did change
  private onFoodHistoryChanged(event: FoodHistoryChangedEvent) {
    switch (event.type) {
      case FoodHistoryChangeType.updated_food_history:
        this.changedSubject.next(new UserChangedEvent(UserChangeType.updated_food_history, event.value));
        break;
      case FoodHistoryChangeType.added_food_record:
        this.changedSubject.next(new UserChangedEvent(UserChangeType.added_food_record, event.value));
        break;
      case FoodHistoryChangeType.added_food_log:
        this.changedSubject.next(new UserChangedEvent(UserChangeType.added_food_log, event.value));
        break;
      case FoodHistoryChangeType.changed_food_log:
        this.changedSubject.next(new UserChangedEvent(UserChangeType.changed_food_log, event.value));
        break;
      case FoodHistoryChangeType.deleted_food_log:
        this.changedSubject.next(new UserChangedEvent(UserChangeType.deleted_food_log, event.value));
        break;
      default:
        console.log('Unknown food history change!');
    }
  }

  // Initialize typical meals
  private initializeTypicalMeals() {
    this._typicalMeals = [{type: MealType.Breakfast}, {type: MealType.Lunch}, {type: MealType.Snack}, {type: MealType.Dinner}];
    this.updateTypicalMealCalories();
  }

  // This is a safety method only necessary for the very first tracked day after account creation.
  // Dashboard is loaded in the background while onboarding is still ongoing and tracked days are not created yet.
  // That means the tracked day will be created without meals. This will then quickly lead to an error when first time

  // Initialize typical calories for breakfast, lunch and so on if not already set manually
  private initializeTypicalMealCalories() {
    if (this.typicalMeals) {
      this.typicalMeals.forEach(meal => {
        meal.typicalCalories = meal.typicalCalories ? meal.typicalCalories : this.calculateTypicalMealCalories(meal);
      });
    }
    this.changedSubject.next(new UserChangedEvent(UserChangeType.changed_typical_meals, this.typicalMeals));
  }

  private updateTypicalMealCalories() {
    if (this.typicalMeals) {
      this._typicalMeals.forEach(meal => {
        meal.typicalCalories = this.calculateTypicalMealCalories(meal);
      });
    }
    this.changedSubject.next(new UserChangedEvent(UserChangeType.changed_typical_meals, this.typicalMeals));
  }

  private calculateTypicalMealCalories(meal: TypicalMeal): number {
    if (meal.typicalCaloriePercentage !== undefined) {
      return meal.typicalCaloriePercentage * this.dailyCalories;
    }
    switch (meal.type) {
      case MealType.Breakfast: {
        return this.typicalBreakfastCaloriePercentage * this.dailyCalories;
      }
      case MealType.Lunch: {
        return this.typicalLunchCaloriePercentage * this.dailyCalories;
      }
      case MealType.Dinner: {
        return this.typicalDinnerCaloriePercentage * this.dailyCalories;
      }
      case MealType.Snack: {
        return this.typicalSnacksCaloriePercentage * this.dailyCalories;
      }
      default: {
        return 0;
      }
    }
  }

  // opening the tracker page.
  private checkCurrentTrackedDayHasNoMealsYet(): void {
    const index = this.getTrackedDay(new Date());
    if (this.trackedDays[index].meals.length > 0) {
      return;
    }
    console.log('Hallo!');
    this.trackedDays.splice(index, 1);
    const newDay = new TrackedDay(undefined, this.typicalMeals);
    newDay.day = todayDateOnly();
    this.trackedDays.push(newDay);
  }

  private updateEmissionsBudgetWithNewCalories(previousCalories: number, newCalories: number) {
    this.typicalDailyEmissions = Math.round(this.typicalDailyEmissions / previousCalories * newCalories);
  }

  // this method is necessary as food logs of recipes didn't have units in the beginning. Now they have and also the

  // old ones must have, because needed for statistics calculation
  private addUnitsForAllRecipeFoodLogs(): void {
    this.foodHistory.records.forEach(record => {
      if (record.type === FoodType.recipe) {
        const recipe = this.recipes.find(r => r.id === record.id);
        if (recipe) {
          record.logs.forEach((log, i) => {
            if (!log.unit || log.unit.conversionToGram === 0) {
              log.unit = recipe.unit;
              this.foodHistory.changeItemLogByIndex(record.id, i, log);
            }
          });
        }
      }
    });
  }

}

export class UserChangedEvent {
  type: UserChangeType;
  value: any;
  dontUpdateChangeId?: boolean;

  constructor(type: UserChangeType, value?: any, dontUpdateChangeId?: boolean) {
    this.type = type;
    if (value) {
      this.value = value;
    }
    this.dontUpdateChangeId = dontUpdateChangeId;
  }
}

export enum UserChangeType {
  changed_typical_meals,
  changed_tracked_day,
  added_tracked_day,
  changed_questionnaire,
  changed_emissions_target,
  added_used_day,
  added_recipe,
  changed_recipe,
  deleted_recipe,
  updated_food_history,
  added_food_record,
  added_food_log,
  changed_food_log,
  deleted_food_log,
  changed_notificaton_settings,
  changed_tutorials_done,
  changed_info_hub_favorites,
  autofill,
  set_five_days_app_usage_feedback_shown,
  merged_local_user_with_backend_account,
  miscellaneous,
  only_storage_update
}

export interface IUser {
  // averageCaloryPercentages: CaloryPercentage;
  typicalMeals: TypicalMeal[];
  typicalMealsAutoSet?: boolean;
  typicalDailyEmissions: number;
  typicalDailyEmissionsAutoSet?: boolean;
  trackedDays: ITrackedDay[];
  questionnaire: IQuestionnaire;
  id?: string;
  isLocalUser?: boolean;
  userName?: string;
  email?: string;
  lastChangedAt?: Date;
  lastChangeId?: string;
  isActive?: boolean;
  emailVerified?: boolean;
  createdAt?: Date;
  identitiyProviderInfos?: IdentitiyProviderInfo[];
  daysUsed?: string[];
  claims?: object[];
  recipes?: IRecipe[];
  foodHistory?: IFoodHistory;
  notificationSettings?: INotificationSettings;
  infoHubFavorites?: IInfoHubFavorites;
  tutorialsDone?: TutorialsDone;
  fiveDaysAppUsageFeedbackShown: boolean;
}

export interface TypicalMeal {
  type: MealType;
  disabled?: boolean;
  typicalCalories?: number;
  typicalCaloriePercentage?: number;
  typicalMealItems?: (IMealFood | IMealRecipe | IMealFoodProduct)[];
}

export interface IdentitiyProviderInfo {
  identityProvider: string;
  userId: string;
}
